import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, Trash2, Pencil, Copy, ExternalLink, Download, Mail, UserPlus, HandHeart, MapPin, Lock, Unlock, ArrowLeft, X } from 'lucide-react';
import { toast } from 'sonner';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { signupsAPI, toLocalInput, fromLocalInput, fmtSpan, fmtWhen } from '@/lib/bulletinAPI';

const red = 'bg-[#A62B1F] hover:bg-[#8C2318] text-white';
const card = 'rounded-xl border border-[#E3DDD1] bg-white';
const serif = { fontFamily: 'Fraunces, Georgia, serif' };
const err = (e, fallback) => toast.error(e?.response?.data?.detail || fallback);

const Fill = ({ filled, capacity }) => {
  const pct = capacity ? Math.min(100, Math.round((filled / capacity) * 100)) : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-24 rounded-full bg-[#F3EFE7] overflow-hidden"><div className="h-full bg-[#A62B1F]" style={{ width: `${pct}%` }} /></div>
      <span className="text-xs text-[#5C6670] whitespace-nowrap">{filled} of {capacity}</span>
    </div>
  );
};

// ── Create / edit ────────────────────────────────────────────────────────────
const blankShift = () => ({ label: '', starts_at: '', ends_at: '', capacity: 2 });

const EventDialog = ({ open, onOpenChange, event, onSaved }) => {
  const [f, setF] = useState({ title: '', description: '', location: '', shifts: [blankShift()] });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setF(event ? {
      title: event.title, description: event.description, location: event.location,
      shifts: event.shifts.map((s) => ({ id: s.id, label: s.label, starts_at: toLocalInput(s.starts_at), ends_at: toLocalInput(s.ends_at), capacity: s.capacity, filled: s.filled })),
    } : { title: '', description: '', location: '', shifts: [blankShift()] });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const setShift = (i, patch) => setF({ ...f, shifts: f.shifts.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  const addShift = () => {
    const last = f.shifts[f.shifts.length - 1];
    // a new shift starts where the last one ended, which is how most rotas are built
    setF({ ...f, shifts: [...f.shifts, { ...blankShift(), label: last?.label || '', starts_at: last?.ends_at || last?.starts_at || '', capacity: last?.capacity || 2 }] });
  };

  const save = async () => {
    if (!f.title.trim()) return toast.error('Give it a title');
    const shifts = f.shifts.filter((s) => s.label.trim() || s.starts_at);
    if (!shifts.length) return toast.error('Add at least one shift');
    if (shifts.some((s) => !s.label.trim() || !s.starts_at)) return toast.error('Every shift needs a name and a start time');
    setSaving(true);
    const data = { title: f.title, description: f.description, location: f.location,
      shifts: shifts.map((s) => ({ id: s.id, label: s.label, starts_at: fromLocalInput(s.starts_at), ends_at: fromLocalInput(s.ends_at), capacity: Number(s.capacity) || 1 })) };
    try {
      const r = event ? await signupsAPI.update(event.id, data) : await signupsAPI.create(data);
      toast.success(event ? 'Saved' : 'Sign-up created');
      onOpenChange(false); onSaved(r.data);
    } catch (e) { err(e, 'Could not save'); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{event ? 'Edit sign-up' : 'New volunteer sign-up'}</DialogTitle>
          <DialogDescription>Volunteers pick a shift from your public page. No account needed.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>What's it for?</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="e.g. Christmas Eve services" /></div>
          <div className="grid sm:grid-cols-2 gap-2">
            <div><Label>Where (optional)</Label><Input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} placeholder="Main hall" /></div>
          </div>
          <div><Label>Details for volunteers (optional)</Label><Textarea rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="What to wear, where to park, who to ask for…" /></div>
          <div>
            <Label>Shifts</Label>
            <div className="space-y-2 mt-1">
              {f.shifts.map((s, i) => (
                <div key={i} className="rounded-lg border border-[#E3DDD1] p-2 grid grid-cols-2 sm:grid-cols-[1.4fr_1fr_1fr_70px_32px] gap-2 items-end">
                  <div className="col-span-2 sm:col-span-1"><span className="text-[11px] text-[#5C6670]">Role</span><Input value={s.label} onChange={(e) => setShift(i, { label: e.target.value })} placeholder="Greeters" /></div>
                  <div><span className="text-[11px] text-[#5C6670]">Starts</span><Input type="datetime-local" value={s.starts_at} onChange={(e) => setShift(i, { starts_at: e.target.value })} /></div>
                  <div><span className="text-[11px] text-[#5C6670]">Ends</span><Input type="datetime-local" value={s.ends_at} onChange={(e) => setShift(i, { ends_at: e.target.value })} /></div>
                  <div><span className="text-[11px] text-[#5C6670]">People</span><Input type="number" min={s.filled || 1} value={s.capacity} onChange={(e) => setShift(i, { capacity: e.target.value })} /></div>
                  <button type="button" title={s.filled ? 'People are signed up for this shift' : 'Remove shift'} disabled={!!s.filled || f.shifts.length === 1}
                    className="h-10 flex items-center justify-center text-[#5C6670] disabled:opacity-30" onClick={() => setF({ ...f, shifts: f.shifts.filter((_, j) => j !== i) })}><X className="w-4 h-4" /></button>
                  {s.filled > 0 && <div className="col-span-2 sm:col-span-5 text-[11px] text-[#5C6670]">{s.filled} signed up</div>}
                </div>
              ))}
              <button type="button" className="text-xs font-semibold text-[#A62B1F]" onClick={addShift}>+ Add another shift</button>
            </div>
          </div>
          <Button onClick={save} disabled={saving} className={`w-full ${red}`}>{saving ? 'Saving…' : event ? 'Save changes' : 'Create sign-up'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ── Detail ───────────────────────────────────────────────────────────────────
const AddVolunteerDialog = ({ open, onOpenChange, event, onAdded }) => {
  const [f, setF] = useState({ shift_id: '', name: '', email: '', phone: '' });
  useEffect(() => { if (open) setF({ shift_id: event.shifts.find((s) => s.remaining > 0)?.id || event.shifts[0]?.id, name: '', email: '', phone: '' }); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Add a volunteer</DialogTitle><DialogDescription>For someone who signed up by phone or on paper. If you add an email, they'll get a confirmation and a reminder.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Shift</Label>
            <select className="w-full h-10 rounded-md border border-input px-2 text-sm bg-white" value={f.shift_id} onChange={(e) => setF({ ...f, shift_id: e.target.value })}>
              {event.shifts.map((s) => <option key={s.id} value={s.id} disabled={s.remaining <= 0}>{s.label} · {fmtWhen(s.starts_at)} {s.remaining <= 0 ? '(full)' : `(${s.remaining} open)`}</option>)}
            </select></div>
          <div><Label>Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Email (optional)</Label><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
            <div><Label>Phone (optional)</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
          </div>
          <Button className={`w-full ${red}`} onClick={async () => {
            try { await signupsAPI.addEntry(event.id, { ...f, email: f.email || null, phone: f.phone || null }); toast.success('Added'); onOpenChange(false); onAdded(); } catch (e) { err(e, 'Could not add'); }
          }}>Add</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const MessageDialog = ({ open, onOpenChange, event }) => {
  const [f, setF] = useState({ subject: '', message: '', shift_id: '' });
  useEffect(() => { if (open) setF({ subject: `About ${event.title}`, message: '', shift_id: '' }); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Email volunteers</DialogTitle><DialogDescription>Goes to everyone signed up who gave an email.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>To</Label>
            <select className="w-full h-10 rounded-md border border-input px-2 text-sm bg-white" value={f.shift_id} onChange={(e) => setF({ ...f, shift_id: e.target.value })}>
              <option value="">Everyone on this sign-up</option>
              {event.shifts.map((s) => <option key={s.id} value={s.id}>Only {s.label} · {fmtWhen(s.starts_at)}</option>)}
            </select></div>
          <div><Label>Subject</Label><Input value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} /></div>
          <div><Label>Message</Label><Textarea rows={6} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} /></div>
          <Button className={`w-full ${red}`} onClick={async () => {
            try { const r = await signupsAPI.message(event.id, { ...f, shift_id: f.shift_id || null }); toast.success(r.data.message); onOpenChange(false); } catch (e) { err(e, 'Could not send'); }
          }}>Send</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const EventDetail = ({ id, onBack, onChanged }) => {
  const [ev, setEv] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const load = useCallback(() => signupsAPI.get(id).then((r) => setEv(r.data)).catch(() => { toast.error('Sign-up not found'); onBack(); }), [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  if (!ev) return <div className={`${card} p-6 text-sm text-[#5C6670]`}>Loading…</div>;
  const active = ev.entries.filter((x) => x.status === 'active');
  const changed = () => { load(); onChanged(); };

  return (
    <div className="space-y-4">
      <div className={`${card} p-4`}>
        <div className="flex items-start gap-2 flex-wrap">
          <button className="p-1 -ml-1" onClick={onBack} title="All sign-ups"><ArrowLeft className="w-4 h-4" /></button>
          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-semibold text-[#1E2A33]" style={serif}>{ev.title}</h2>
            <div className="flex items-center gap-3 flex-wrap mt-1 text-xs text-[#5C6670]">
              {ev.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{ev.location}</span>}
              <span className={`font-semibold px-2 py-0.5 rounded-full ${ev.status === 'open' && !ev.past ? 'bg-emerald-50 text-emerald-800' : 'bg-[#F3EFE7] text-[#5C6670]'}`}>{ev.past ? 'Finished' : ev.status === 'open' ? 'Taking sign-ups' : 'Closed'}</span>
              <Fill filled={ev.filled} capacity={ev.capacity} />
            </div>
          </div>
          <div className="flex gap-1 flex-wrap">
            <Button size="sm" variant="outline" onClick={() => setAddOpen(true)}><UserPlus className="w-4 h-4 mr-1" />Add</Button>
            <Button size="sm" variant="outline" onClick={() => setMsgOpen(true)} disabled={!active.some((x) => x.email)}><Mail className="w-4 h-4 mr-1" />Email</Button>
            <Button size="sm" variant="ghost" title="Download CSV" onClick={async () => {
              const r = await signupsAPI.exportCsv(ev.id);
              const url = URL.createObjectURL(r.data); const a = document.createElement('a');
              a.href = url; a.download = `${ev.title.replace(/[^A-Za-z0-9]+/g, '-')}.csv`; document.body.appendChild(a); a.click(); a.remove();
              setTimeout(() => URL.revokeObjectURL(url), 2000);
            }}><Download className="w-4 h-4" /></Button>
            <Button size="sm" variant="ghost" title="Edit" onClick={() => setEditOpen(true)}><Pencil className="w-4 h-4" /></Button>
            <Button size="sm" variant="ghost" title={ev.status === 'open' ? 'Close sign-ups' : 'Reopen sign-ups'} onClick={async () => { await signupsAPI.update(ev.id, { status: ev.status === 'open' ? 'closed' : 'open' }); toast.success(ev.status === 'open' ? 'Closed. No new sign-ups.' : 'Open again'); changed(); }}>{ev.status === 'open' ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}</Button>
            <Button size="sm" variant="ghost" title="Delete" className="text-[#A62B1F]" onClick={async () => { if (!window.confirm(`Delete “${ev.title}” and its roster? Volunteers won't be told.`)) return; await signupsAPI.remove(ev.id); toast.success('Deleted'); onChanged(); onBack(); }}><Trash2 className="w-4 h-4" /></Button>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <span className="text-xs text-[#5C6670]">Sign-up link:</span>
          <code className="px-2 py-1 rounded bg-[#FBF8F2] border border-[#E3DDD1] text-xs break-all">{ev.public_url}</code>
          <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(ev.public_url); toast.success('Link copied. Share it in an email, on social or in the bulletin.'); }}><Copy className="w-4 h-4 mr-1" />Copy</Button>
          <Button size="sm" variant="ghost" onClick={() => window.open(ev.public_url, '_blank')}><ExternalLink className="w-4 h-4" /></Button>
        </div>
        {ev.description && <p className="text-sm text-[#33404A] mt-3 whitespace-pre-line">{ev.description}</p>}
      </div>

      {ev.shifts.map((s) => {
        const people = active.filter((x) => x.shift_id === s.id);
        return (
          <div key={s.id} className={`${card} p-4`}>
            <div className="flex items-center gap-3 flex-wrap mb-2">
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-[#1E2A33]">{s.label}</div>
                <div className="text-xs text-[#5C6670]">{fmtSpan(s.starts_at, s.ends_at)}</div>
              </div>
              <Fill filled={s.filled} capacity={s.capacity} />
            </div>
            {people.length === 0 ? <p className="text-sm text-[#5C6670]">No one yet.</p> : (
              <ul className="divide-y divide-[#E3DDD1]">
                {people.map((x) => (
                  <li key={x.id} className="py-2 flex items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium">{x.name}{x.source === 'team' && <span className="ml-2 text-[11px] text-[#5C6670] font-normal">added by team</span>}</div>
                      <div className="text-xs text-[#5C6670] truncate">{[x.email, x.phone].filter(Boolean).join(' · ')}{x.note ? ` · “${x.note}”` : ''}</div>
                    </div>
                    <Button size="sm" variant="ghost" className="text-[#A62B1F]" title="Remove" onClick={async () => { if (!window.confirm(`Take ${x.name} off ${s.label}? They won't be emailed.`)) return; await signupsAPI.removeEntry(ev.id, x.id); toast.success('Removed. The spot is open again.'); changed(); }}><Trash2 className="w-4 h-4" /></Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
      {ev.entries.some((x) => x.status === 'cancelled') && (
        <div className={`${card} p-4`}>
          <div className="text-sm font-semibold mb-1">Cancelled</div>
          <ul className="text-xs text-[#5C6670] space-y-0.5">
            {ev.entries.filter((x) => x.status === 'cancelled').map((x) => <li key={x.id}>{x.name} · {ev.shifts.find((s) => s.id === x.shift_id)?.label}</li>)}
          </ul>
        </div>
      )}
      <EventDialog open={editOpen} onOpenChange={setEditOpen} event={ev} onSaved={changed} />
      <AddVolunteerDialog open={addOpen} onOpenChange={setAddOpen} event={ev} onAdded={changed} />
      <MessageDialog open={msgOpen} onOpenChange={setMsgOpen} event={ev} />
    </div>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────
export default function Signups() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [showPast, setShowPast] = useState(false);
  const load = useCallback(() => signupsAPI.list().then((r) => setRows(r.data)).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);
  const shown = (rows || []).filter((e) => showPast || !e.past);

  return (
    <Layout title="Sign-ups">
      {id ? <EventDetail id={id} onBack={() => navigate('/signups')} onChanged={load} /> : (
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-2xl font-semibold text-[#1E2A33]" style={serif}>Sign-ups</h1>
              <p className="text-sm text-[#5C6670]">Fill volunteer shifts. People sign up from a link, and get a reminder the day before.</p>
            </div>
            <Button className={red} onClick={() => setNewOpen(true)}><Plus className="w-4 h-4 mr-1" />New sign-up</Button>
          </div>
          <div className={`${card}`}>
            {rows === null ? <p className="p-4 text-sm text-[#5C6670]">Loading…</p> : shown.length === 0 ? (
              <div className="p-10 text-center text-sm text-[#5C6670]"><HandHeart className="w-8 h-8 mx-auto mb-2 text-[#A62B1F]" />No upcoming sign-ups. Create one for your next service or event.</div>
            ) : (
              <ul className="divide-y divide-[#E3DDD1]">
                {shown.map((e) => (
                  <li key={e.id}>
                    <button className="w-full text-left p-4 hover:bg-[#FBF8F2] flex items-center gap-3 flex-wrap" onClick={() => navigate(`/signups/${e.id}`)}>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-[#1E2A33]">{e.title}</div>
                        <div className="text-xs text-[#5C6670]">{fmtWhen(e.starts_at)} · {e.shifts.length} shift{e.shifts.length === 1 ? '' : 's'}{e.location ? ` · ${e.location}` : ''}</div>
                      </div>
                      {e.past ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#F3EFE7] text-[#5C6670]">Finished</span>
                        : e.status === 'closed' ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#F3EFE7] text-[#5C6670]">Closed</span> : null}
                      <Fill filled={e.filled} capacity={e.capacity} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {rows?.some((e) => e.past) && <button className="text-xs font-semibold text-[#A62B1F]" onClick={() => setShowPast(!showPast)}>{showPast ? 'Hide finished sign-ups' : 'Show finished sign-ups'}</button>}
          <EventDialog open={newOpen} onOpenChange={setNewOpen} onSaved={(e) => { load(); navigate(`/signups/${e.id}`); }} />
        </div>
      )}
    </Layout>
  );
}
